import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { CTASection } from "@/components/CTASection";
import { FEATURES, TRUST_POINTS, SITE } from "@/lib/site";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import { Check } from "lucide-react";
import heroImg from "@/assets/diamond-grinding.jpg";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About MLC Epoxy Floors | Calgary Region Floor Specialists" },
      { name: "description", content: "Airdrie-based specialists in epoxy and polyaspartic floor coatings. Locally owned, fully insured, serving the Calgary region." },
      { property: "og:title", content: "About | MLC Epoxy Floors" },
      { property: "og:description", content: "Locally owned, Airdrie-based garage floor coating specialists serving the Calgary region." },
      { property: "og:url", content: "/about" },
    ],
    links: [{ rel: "canonical", href: "/about" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbs([{ name: "Home", href: "/" }, { name: "About", href: "/about" }])) },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <>
      <PageHero eyebrow="Our Story" title="About MLC Epoxy Floors — Calgary Region Floor Specialists" body={`MLC Epoxy Floors is an Airdrie, Alberta-based specialist focused exclusively on premium epoxy and polyaspartic floor coatings for the entire Calgary region.`} image={heroImg} />

      <section className="container-x py-20 grid gap-12 lg:grid-cols-[2fr_1fr]">
        <article className="prose-invert space-y-6 text-silver leading-relaxed text-lg">
          <h2 className="text-3xl text-silver-light">A Specialist, Not A Generalist</h2>
          <span className="copper-rule" />
          <p>MLC Epoxy Floors was founded on a simple premise: garage floor coatings deserve a specialist. Too many homeowners across the Calgary region have been burned by generalist contractors, big-box installers, and franchise operators who acid-etch concrete, cut corners on prep, and use consumer-grade materials. The result is a floor that looks great for six months and starts peeling in twelve.</p>
          <p>We do one thing — premium concrete coatings — and we do it to a single standard. Every project starts with industrial diamond grinding, uses commercial-grade resins, and finishes with a UV-stable polyaspartic topcoat. There are no entry-level options, no shortcuts, and no exceptions.</p>

          <h2 className="text-3xl text-silver-light mt-12">Our Crew</h2>
          <span className="copper-rule" />
          <p>Every member of our installation team is a W-2 employee we trained personally. We do not subcontract. The crew that shows up at your home is the same crew that installed the floor you saw in our portfolio. We have a vested interest in every square foot we coat — and it shows in the workmanship.</p>

          <h2 className="text-3xl text-silver-light mt-12">Our Standards</h2>
          <span className="copper-rule" />
          <p>We carry full liability and workers' compensation insurance. Every residential floor is backed by a written workmanship warranty against peeling, delamination, and hot tire pickup. We provide fixed-price written quotes — no estimates that climb on installation day. If we cannot do it right, we do not do it at all.</p>

          <h2 className="text-3xl text-silver-light mt-12">Why The Calgary Region Trusts Us</h2>
          <span className="copper-rule" />
          <p>Calgary homeowners, Calgary luxury garages, Calgary professionals, Calgary commercial spaces, and warehouses across Calgary area all choose MLC Epoxy Floors for the same reason: we treat every floor like it is going in our own home. Locally owned, locally installed, locally accountable.</p>
        </article>

        <aside className="space-y-6">
          <div className="rounded-sm border border-border bg-charcoal p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-copper">Our Standards</p>
            <ul className="mt-4 space-y-2.5 text-sm text-silver">
              {TRUST_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-copper shrink-0" /> {p}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-sm border border-border bg-charcoal p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-copper">Headquarters</p>
            <p className="mt-3 text-silver-light text-sm">{SITE.address.street}<br />{SITE.address.city}, {SITE.address.region} {SITE.address.postal}</p>
            <p className="mt-3 text-silver text-sm">{SITE.hours}</p>
          </div>
        </aside>
      </section>

      <section className="border-t border-border bg-charcoal/60 py-20">
        <div className="container-x">
          <SectionHeading eyebrow="What Sets Us Apart" title="Nine Things We Do Differently" />
          <div className="mt-12 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3 border border-border">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-jet p-7">
                <p className="text-xs font-bold uppercase tracking-widest text-copper">{f.title}</p>
                <p className="mt-3 text-silver text-sm">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
